<script setup lang="ts">
  import { computed, nextTick, onBeforeMount, ref, toRef } from 'vue';

  import type { Email, Task } from '@stuut/api/models';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { HoverCard, HoverCardTrigger } from '@ui/components/hover-card';

  import SuggestedReplyPopover from '@/components/emails/SuggestedReplyPopover.vue';
  import { useEmailResponseTasks } from '@/composables/useEmailResponseTasks';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    email: Email;
    threadEmails: Email[];
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'draft-created', email: Email): void;
    (e: 'draft-replaced', email: Email): void;
  }>();

  const taskCollectionStore = useTaskCollectionStore();

  const {
    applyTaskAsDraft,
    fullResponseTasks: loadedTasks,
    isApplying: isCreatingDraft,
    loadResponseTasks,
  } = useEmailResponseTasks(toRef(props, 'email'));

  const fullResponseTasks = computed(() => loadedTasks.value || []);

  const showReplaceDialog = ref(false);
  const pendingTask = ref<Task>();

  const existingDraft = computed(() =>
    props.threadEmails.find((e) => !e.isSent && e.parent_email_id === props.email.id),
  );

  onBeforeMount(() => loadResponseTasks());

  const applyOrConfirm = async (task: Task) => {
    if (existingDraft.value) {
      pendingTask.value = task;
      showReplaceDialog.value = true;
      return;
    }
    const draft = await applyTaskAsDraft(task);
    if (draft) emit('draft-created', draft);
  };

  const confirmCreateNew = async () => {
    if (!pendingTask.value) return;
    showReplaceDialog.value = false;
    const task = pendingTask.value;
    pendingTask.value = undefined;
    const draft = await applyTaskAsDraft(task);
    if (draft) emit('draft-created', draft);
  };

  const confirmReplace = async () => {
    if (!pendingTask.value || !existingDraft.value) return;
    showReplaceDialog.value = false;
    const task = pendingTask.value;
    pendingTask.value = undefined;
    const updated = await applyTaskAsDraft(task, existingDraft.value);
    if (updated) emit('draft-replaced', updated);
  };

  const openTask = (task: Task) => {
    nextTick(() =>
      setTimeout(() => {
        taskCollectionStore.openTaskByQuery?.(task);
      }, 0),
    );
  };
</script>

<template>
  <div class="flex flex-col" :data-email-suggestion="email.id">
    <div v-for="tt in fullResponseTasks" :key="tt.id" class="relative overflow-hidden group py-4">
      <div class="relative z-10 flex gap-3 xl:gap-x-10">
        <div class="flex-1 min-w-0 flex flex-col gap-y-2">
          <div
            class="flex items-center flex-1 justify-between text-muted-foreground"
            @click="openTask(tt)"
          >
            <div class="flex flex-col gap-y-2 cursor-pointer">
              <span class="text-2xs uppercase tracking-widest font-semibold">Email response</span>

              <p class="text-xs truncate">
                {{ tt.label }}
              </p>
            </div>

            <div class="flex flex-nowrap items-center gap-2 mt-0.5" @click.stop>
              <HoverCard :open-delay="100">
                <HoverCardTrigger as-child>
                  <div class="flex flex-nowrap items-center gap-x-0.5">
                    <Button
                      size="sm"
                      variant="ghost"
                      data-draft-action
                      :loading="isCreatingDraft[tt.id]"
                      class="gap-x-1.5 opacity-0 group-hover:opacity-100 pr-0 group-hover:pr-2 transition-all"
                      @click.stop.prevent="applyOrConfirm(tt)"
                    >
                      <i class="fa-solid fa-sparkles text-2xs" />
                      <span>Draft with task reply</span>
                    </Button>
                  </div>
                </HoverCardTrigger>

                <SuggestedReplyPopover
                  :visible-suggestions="[tt]"
                  :overflow-count="0"
                  :total-count="1"
                  :is-applying="isCreatingDraft"
                  :can-blank-reply="false"
                  @use="applyOrConfirm"
                />
              </HoverCard>

              <div class="flex items-center flex-nowrap justify-end gap-x-0.5 pr-0.5">
                <Button
                  variant="transparent"
                  size="sm"
                  class="opacity-0 text-nowrap group-hover:opacity-100 group-hover:underline group-has-[[data-draft-action]:hover]:!no-underline group-hover:pr-2 transition-all"
                  @click="openTask(tt)"
                  >View task</Button
                >

                <i
                  class="fa fa-arrow-right text-muted-foreground/60 text-xs shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AlertDialog :open="showReplaceDialog" @update:open="(v) => (showReplaceDialog = v)">
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Existing draft found</AlertDialogTitle>
          <AlertDialogDescription>
            There is already a draft reply for this email. Would you like to replace it with this
            response or create a new draft?
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel @click="showReplaceDialog = false">Cancel</AlertDialogCancel>
          <Button variant="outline" @click="confirmCreateNew">Create New</Button>
          <AlertDialogAction @click="confirmReplace">Replace</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>
</template>
